import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {DataStatus, useTranslationContext} from 'platform/components';

import {useEffect, useMemo, useRef, useState, type MouseEvent} from 'react';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useOnMount} from 'shared/web';

import {useHttpCalls} from './api/useHttpCalls';
import type {DataGridContext} from './context/DataGridContext';
import {DataGridContextProvider} from './context/useDataGridContext';
import {DataGridErrorContextProvider} from './context/useDataGridErrorContext';
import {EagGridTable} from './EagGridTable';
import {useAvailableHeightPx} from './hooks/useAvailableHeightPx';
import {useAvailableWidthPx} from './hooks/useAvailableWidthPx';
import {useFilterHydration} from './hooks/useFilterHydration';
import {useIsMobileView} from './hooks/useIsMobileView';
import {useLoadPresets} from './hooks/useLoadPresets';
import {
  selectActivePreset,
  selectApiError,
  selectCustomPresets,
  selectDataQuery,
  selectDefinition,
  selectVirtualPreset,
} from './store/createEagGridStore';
import {EagGridStoreProvider} from './store/EagGridStoreProvider';
import {selectWrapperStyle} from './store/selectors/useBehavior';
import {useEagGridSelector} from './store/useEagGridSelector';
import {useEagGridStore} from './store/useEagGridStore';
import type {ActionCallback} from './types/ActionCallback';
import type {EagGridProps} from './types/EagGridProps';
import type {EagGridRef} from './types/EagGridRef';
import {getApiErrorMessage} from './utils/getApiErrorMessage';
import {getContainerStyle, getWrapperClassName} from './utils/getContainerStyle';
import {transformDefinition} from './utils/transformDefinition';
import {getSearchParamsByGridCode} from './utils/url/getSearchParamsByGridCode';
import {setSearchParamsByGridCode} from './utils/url/setSearchParamsByGridCode';

const PREVENT_CONTEXT_MENU = (e: MouseEvent) => {
  e.preventDefault();
};

const DESKTOP_BOTTOM_INSET_PX = 16;

function EagGridInner(props: EagGridProps<readonly string[]>) {
  const t = useTranslationContext();
  const innerRef = useRef<Pick<EagGridRef, 'refreshData'>>(null);
  const store = useEagGridStore();
  const isMobile = useIsMobileView();

  const {availableHeightPx, callbackRef} = useAvailableHeightPx({
    bottomInset: isMobile ? 0 : DESKTOP_BOTTOM_INSET_PX,
  });

  const {availableWidthPx, callbackRef: widthCallbackRef} = useAvailableWidthPx();

  useOnMount(() => {
    store.initStaticConfig({
      gridCode: props.gridCode,
      presetInstanceId: props.presetInstanceId,
      disableAllActions: props.disableAllActions,
    });
  });

  const dataQuery = useEagGridSelector(selectDataQuery);
  const customPresets = useEagGridSelector(selectCustomPresets);
  const virtualPreset = useEagGridSelector(selectVirtualPreset);
  const definition = useEagGridSelector(selectDefinition);
  const activePreset = useEagGridSelector(selectActivePreset);
  const apiError = useEagGridSelector(selectApiError);
  const wrapperStyle = useEagGridSelector(selectWrapperStyle);

  const onApiError = (error: FetchBaseQueryError) => {
    const errorMessage = getApiErrorMessage(error, t('general.notifications.defaultErrorMessage'));
    store.setApiError(errorMessage);
  };

  const isFilterHydrated = useFilterHydration({
    dataQuery,
    queryModifier: props.queryModifier,
    initialSmartSearch: props.initialSmartSearch,
    store,
  });

  // customPresets starts as null (not yet loaded) and transitions to Preset[] after fetch.
  // activePreset is only set after both customPresets and virtualPreset are ready,
  // so !activePreset already subsumes any "customPresets not yet loaded" state.
  const isDatagridLoading =
    !virtualPreset ||
    !definition ||
    !activePreset ||
    !dataQuery ||
    !isFilterHydrated ||
    isNotNil(apiError);

  const http = useHttpCalls({
    gridCode: props.gridCode,
    presetInstanceId: props.presetInstanceId,
    onApiError,
  });

  const loadPresets = useLoadPresets({
    gridCode: props.gridCode,
    presetInstanceId: props.presetInstanceId,
    onApiError,
    queryModifier: props.queryModifier,
  });

  // Refs for volatile deps that must NOT trigger re-fetches when their identity changes.
  // Only gridCode change should restart the fetch cycle.
  const loadPresetsRef = useRef(loadPresets);
  const httpRef = useRef(http);
  const externalFilterResourceIdRef = useRef(props.externalFilterResourceId);
  const externalFilterRecordIdRef = useRef(props.externalFilterRecordId);
  const onApiErrorRef = useRef(onApiError);

  /**
   * Sync latest callback/value references for use in stable-identity effects below.
   *
   * Why useEffect instead of direct assignment in render:
   * React Compiler requires render to be pure — no side effects including ref writes.
   * Effects fire in declaration order within a component, so this sync effect runs
   * BEFORE the consuming effects declared below. All consumers are either
   * local effects or async callbacks (.then/.catch) that execute well after effects fire.
   *
   * Performance: zero impact — no DOM reads, no subscriptions, just pointer assignments.
   *
   * @see Vercel advanced-event-handler-refs pattern
   * @see https://react.dev/reference/react/useRef#caveats
   */
  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs refs every render; see JSDoc above
  useEffect(() => {
    loadPresetsRef.current = loadPresets;
    httpRef.current = http;
    externalFilterResourceIdRef.current = props.externalFilterResourceId;
    externalFilterRecordIdRef.current = props.externalFilterRecordId;
    onApiErrorRef.current = onApiError;
  });

  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive initialization; must re-run if gridCode changes at runtime; async chain (loadPresets + getVirtualPreset + getDefinition) cannot be expressed as event handler
  useEffect(() => {
    loadPresetsRef.current().then((response) => response && store.setCustomPresets(response));
    httpRef.current
      .getVirtualPreset()
      .then((response) => response && store.setVirtualPreset(response));
    httpRef.current.getDefinition({loadChoiceFilterOptions: false}).then((response) => {
      if (!response) {
        return;
      }

      store.setDefinition(response);
    });
  }, [props.gridCode, store]);

  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive data fetch; fires once both customPresets and virtualPreset become non-null; sequential async chain resolving active preset and dataQuery
  useEffect(() => {
    if (virtualPreset && customPresets) {
      // Read searchParams at call time — not a reactive dep, avoids spurious re-runs.
      const currentSearchParams = getSearchParamsByGridCode(props.gridCode);
      httpRef.current.getActivePreset(virtualPreset, customPresets).then((resolvedActivePreset) => {
        if (!resolvedActivePreset) {
          return;
        }

        store.setActivePreset(resolvedActivePreset);
        if (isNilOrEmpty(currentSearchParams)) {
          setSearchParamsByGridCode(props.gridCode, resolvedActivePreset.dataQueryId);
        }
        httpRef.current
          .getDataQuery(currentSearchParams?.queryId ?? resolvedActivePreset.dataQueryId)
          .then((resolvedDataQuery) => resolvedDataQuery && store.setDataQuery(resolvedDataQuery))
          .catch((error: FetchBaseQueryError) => {
            if (isNotNil(currentSearchParams?.queryId)) {
              setSearchParamsByGridCode(props.gridCode, resolvedActivePreset.dataQueryId);

              httpRef.current
                .getDataQuery(resolvedActivePreset.dataQueryId)
                .then(
                  (resolvedDataQuery) => resolvedDataQuery && store.setDataQuery(resolvedDataQuery)
                )
                .catch(onApiErrorRef.current);
            } else {
              onApiErrorRef.current(error);
            }
          });
      });
    }
  }, [customPresets, virtualPreset, store, props.gridCode]);

  /**
   * @mandatory Context.Provider value must be referentially stable.
   * Without useMemo, every EagGridInner render creates a new object reference,
   * causing all DataGridContext consumers to re-render regardless of actual changes.
   */
  const contextValue: DataGridContext = useMemo(
    () => ({
      externalFilterId: props.externalFilterId,
      onExternalFilterChange: props.onExternalFilterChange,
      queryModifier: props.queryModifier,
      filterComponent: props.filterComponent,
    }),
    [
      props.externalFilterId,
      props.onExternalFilterChange,
      props.queryModifier,
      props.filterComponent,
    ]
  );

  /**
   * @mandatory Context.Provider value — same rationale as contextValue above.
   * onApiError is a plain function but its identity changes on every render;
   * wrapping in useMemo prevents all error context consumers from re-rendering.
   */
  const errorContextValue = useMemo(() => ({onApiError}), [onApiError]);

  const dataTestId = props['data-testid'];
  const columnConfig =
    definition && activePreset && dataQuery
      ? transformDefinition({
          definition,
          activePreset,
          dataQuery,
          'data-testid': dataTestId,
        })
      : null;

  const wrapperNodeRef = useRef<HTMLDivElement | null>(null);

  /**
   * parentHeight is stored as state so containerStyle useMemo has complete, reactive deps.
   *
   * React Compiler flags `wrapperNodeRef.current?.parentElement?.offsetHeight` inside useMemo
   * as "Cannot access refs during render". Capturing the value as state in the callback ref
   * (which fires once on mount when the node attaches) avoids the ref read during render.
   *
   * @see Task 11 in docs/plans/2026-03-12-eag-grid-react-compiler-lint-fixes.md
   */
  const [parentHeight, setParentHeight] = useState(0);

  const composedCallbackRef = (node: HTMLDivElement | null) => {
    wrapperNodeRef.current = node;
    setParentHeight(node?.parentElement?.offsetHeight ?? 0);
    callbackRef(node); // height measurement
    widthCallbackRef(node); // width measurement
  };

  const containerStyle = getContainerStyle(
    props.autoHeight ?? false,
    availableHeightPx,
    parentHeight,
    availableWidthPx
  );

  if (isNotNil(apiError)) {
    return (
      <DataStatus isError errorMessage={apiError} minHeight={props.autoHeight ? 20 : '100%'} />
    );
  }

  if (isDatagridLoading || !definition || !columnConfig) {
    return <DataStatus isLoading minHeight={props.autoHeight ? 20 : '100%'} />;
  }

  return (
    <div
      ref={composedCallbackRef}
      className={getWrapperClassName(wrapperStyle, props.autoHeight ?? false, isMobile)}
      style={containerStyle}
      onContextMenu={PREVENT_CONTEXT_MENU}
    >
      <DataGridErrorContextProvider value={errorContextValue}>
        <DataGridContextProvider value={contextValue}>
          <EagGridTable
            ref={innerRef}
            gridCode={props.gridCode}
            presetInstanceId={props.presetInstanceId}
            emptyState={props.emptyState}
            actionCallback={props.actionCallback as ActionCallback}
            onRowSelectionChange={props.onRowSelectionChange}
            externalFilterId={props.externalFilterId}
            autoHeight={props.autoHeight}
            queryModifier={props.queryModifier}
            definition={definition}
            columnConfig={columnConfig}
            onFilterChanged={props.onFilterChanged}
            data-testid={props['data-testid']}
            setVirtualPreset={store.setVirtualPreset}
            predefinedColumns={props.predefinedColumns ?? []}
          />
        </DataGridContextProvider>
      </DataGridErrorContextProvider>
    </div>
  );
}

export function EagGrid<T extends readonly string[] = never>(props: EagGridProps<T>) {
  // The cast to EagGridProps<readonly string[]> is safe — EagGridInner is private and the public
  // generic constraint is enforced at the EagGrid call site.
  return (
    <EagGridStoreProvider>
      <EagGridInner {...(props as EagGridProps<readonly string[]>)} />
    </EagGridStoreProvider>
  );
}
